import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, PencilSimple, Trash, SignOut, X, Package, Receipt, UploadSimple, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { adminApi, formatINR, API } from "@/lib/api";

const EMPTY = { name: "", category: "Unisex", price: "", description: "", fabric: "", images: [], sizes: "S,M,L,XL", featured: false };

const AdminDashboard = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [fulfil, setFulfil] = useState("processing");
  const [note, setNote] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const openOrder = async (id) => {
    try {
      const { data } = await adminApi.get(`/orders/${id}`);
      setOrderDetail(data);
      setFulfil(data.fulfillment_status || "processing");
      setNote("");
      setOrderModal(true);
    } catch {
      toast.error("Could not load order");
    }
  };

  const saveOrderStatus = async () => {
    setSavingStatus(true);
    try {
      await adminApi.post(`/orders/${orderDetail.id}/status`, { fulfillment_status: fulfil, note });
      toast.success(`Customer notified — order ${fulfil}`);
      setOrderModal(false);
      loadOrders();
    } catch {
      toast.error("Update failed");
    } finally {
      setSavingStatus(false);
    }
  };

  const loadProducts = useCallback(() => adminApi.get("/products").then((r) => setProducts(r.data)), []);
  const loadOrders = useCallback(() => adminApi.get("/orders").then((r) => setOrders(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (!localStorage.getItem("tamashii_token")) {
      nav("/admin/login");
      return;
    }
    adminApi.get("/auth/me").catch(() => {
      localStorage.removeItem("tamashii_token");
      nav("/admin/login");
    });
    loadProducts();
    loadOrders();
  }, [nav, loadProducts, loadOrders]);

  const logout = () => {
    localStorage.removeItem("tamashii_token");
    nav("/admin/login");
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setModal(true); };
  const openEdit = (p) => {
    const images = p.images && p.images.length ? p.images : p.image ? [p.image] : [];
    setForm({ ...p, images, price: String(p.price), sizes: (p.sizes || []).join(",") });
    setEditId(p.id);
    setModal(true);
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await adminApi.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const urls = data.paths.map((p) => `${API}/files/${p}`);
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(`${urls.length} image(s) uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  const makePrimary = (idx) =>
    setForm((prev) => {
      const imgs = [...prev.images];
      const [pick] = imgs.splice(idx, 1);
      return { ...prev, images: [pick, ...imgs] };
    });

  const [dragIdx, setDragIdx] = useState(null);
  const onDrop = (dropIdx) => {
    setForm((prev) => {
      if (dragIdx === null || dragIdx === dropIdx) return prev;
      const imgs = [...prev.images];
      const [moved] = imgs.splice(dragIdx, 1);
      imgs.splice(dropIdx, 0, moved);
      return { ...prev, images: imgs };
    });
    setDragIdx(null);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.images.length) {
      toast.error("Please add at least one image");
      return;
    }
    const payload = {
      name: form.name,
      category: form.category,
      price: parseFloat(form.price) || 0,
      description: form.description,
      fabric: form.fabric,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      images: form.images,
      image: form.images[0],
      featured: form.featured,
    };
    try {
      if (editId) {
        await adminApi.put(`/products/${editId}`, payload);
        toast.success("Product updated");
      } else {
        await adminApi.post("/products", payload);
        toast.success("Product created");
      }
      setModal(false);
      loadProducts();
    } catch {
      toast.error("Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await adminApi.delete(`/products/${id}`);
    toast.success("Product deleted");
    loadProducts();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C]">
      <div className="grain" />
      <header className="border-b border-line px-6 md:px-10 h-[72px] flex items-center justify-between sticky top-0 bg-[#0A0A0C]/90 backdrop-blur-xl z-40">
        <Link to="/" className="font-serif text-2xl tracking-[0.12em] text-bone">TAMASHii <span className="font-mono text-xs text-crimson tracking-normal ml-2">ATELIER</span></Link>
        <button onClick={logout} data-testid="admin-logout" className="overline flex items-center gap-2 text-ash hover:text-crimson">
          Sign Out <SignOut size={16} />
        </button>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline mb-3">Dashboard</p>
            <h1 className="font-serif text-5xl md:text-6xl text-bone">Manage Store</h1>
          </div>
          {tab === "products" && (
            <button onClick={openNew} data-testid="add-product-button" className="btn-fill flex items-center gap-2 px-6 py-3 overline text-xs">
              <Plus size={16} /> New Piece
            </button>
          )}
        </div>

        <div className="flex gap-1 mb-10 border-b border-line">
          {[["products", "Products", Package], ["orders", "Orders", Receipt]].map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className={`flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                tab === k ? "border-crimson text-bone" : "border-transparent text-ash hover:text-bone"
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="border border-line">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-line font-mono text-[10px] uppercase tracking-widest text-ash">
              <div className="col-span-1">Image</div>
              <div className="col-span-4">Name</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Price</div>
              <div className="col-span-1">Featured</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-line items-center hover:bg-ink-800/40">
                <div className="col-span-2 md:col-span-1"><img src={p.image} alt={p.name} className="w-12 h-16 object-cover" /></div>
                <div className="col-span-10 md:col-span-4 font-serif text-xl text-bone">{p.name}</div>
                <div className="col-span-4 md:col-span-2 font-mono text-xs text-ash uppercase">{p.category}</div>
                <div className="col-span-4 md:col-span-2 text-bone">{formatINR(p.price)}</div>
                <div className="col-span-2 md:col-span-1 font-mono text-xs">{p.featured ? <span className="text-gold">YES</span> : <span className="text-ash">—</span>}</div>
                <div className="col-span-2 md:col-span-2 flex justify-end gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 border border-line text-ash hover:text-bone hover:border-bone"><PencilSimple size={14} /></button>
                  <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-2 border border-line text-ash hover:text-crimson hover:border-crimson"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="border border-line">
            <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-line font-mono text-[10px] uppercase tracking-widest text-ash">
              <div className="col-span-4">Items</div>
              <div className="col-span-2">Amount</div>
              <div className="col-span-2">Payment</div>
              <div className="col-span-2">Fulfilment</div>
              <div className="col-span-2">Date</div>
            </div>
            {orders.length === 0 && <p className="p-8 font-serif text-2xl text-ash italic">No orders yet.</p>}
            {orders.map((o) => (
              <button key={o.id} onClick={() => openOrder(o.id)} data-testid={`order-${o.id}`} className="w-full text-left grid grid-cols-12 gap-4 px-5 py-4 border-b border-line items-center hover:bg-ink-800/40 transition-colors">
                <div className="col-span-12 md:col-span-4 text-bone text-sm truncate">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</div>
                <div className="col-span-4 md:col-span-2 text-bone">{formatINR(o.amount)}</div>
                <div className="col-span-4 md:col-span-2">
                  <span className={`font-mono text-[10px] uppercase px-2 py-1 border ${o.payment_status === "paid" ? "text-gold border-gold" : "text-ash border-line"}`}>{o.payment_status}</span>
                </div>
                <div className="col-span-4 md:col-span-2">
                  <span className="font-mono text-[10px] uppercase text-ash">{o.fulfillment_status || "—"}</span>
                </div>
                <div className="col-span-4 md:col-span-2 font-mono text-xs text-ash">{new Date(o.created_at).toLocaleDateString()}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {orderModal && orderDetail && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#141416] border border-line w-full max-w-2xl my-8" data-testid="order-detail-modal">
            <div className="flex items-center justify-between p-6 border-b border-line">
              <p className="overline">Order Detail</p>
              <button onClick={() => setOrderModal(false)} className="text-ash hover:text-bone"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="overline mb-2">Customer Email</p>
                  <p className="text-bone text-sm font-mono" data-testid="order-detail-email">{orderDetail.email || "—"}</p>
                </div>
                <div>
                  <p className="overline mb-2">Placed</p>
                  <p className="text-bone text-sm font-mono">{new Date(orderDetail.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div>
                <p className="overline mb-3">Items</p>
                <div className="border border-line divide-y divide-line">
                  {(orderDetail.items || []).map((i, idx) => (
                    <div key={idx} className="flex justify-between px-4 py-3">
                      <span className="text-bone text-sm">{i.name} <span className="text-ash">· {i.size} × {i.qty}</span></span>
                      <span className="text-ash text-sm">{formatINR(i.price * i.qty)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between px-4 py-3 bg-ink-800/40">
                    <span className="overline">Total</span>
                    <span className="font-serif text-xl text-bone">{formatINR(orderDetail.amount)}</span>
                  </div>
                </div>
              </div>

              <div>
                <p className="overline mb-3">Shipping Address</p>
                {orderDetail.addresses && orderDetail.addresses.length ? (
                  <div className="space-y-3">
                    {orderDetail.addresses.map((a) => (
                      <div key={a.id} className="border border-line p-4" data-testid="order-detail-address">
                        <p className="font-mono text-[10px] uppercase text-crimson mb-1">{a.label}</p>
                        <p className="text-bone text-sm">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}{a.state ? `, ${a.state}` : ""} — {a.pincode}</p>
                        {a.phone && <p className="font-mono text-xs text-ash mt-1">{a.phone}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-ash text-sm italic font-serif">No saved address (guest checkout or none on file). Reach the customer at their email.</p>
                )}
              </div>

              <div className="border-t border-line pt-6">
                <p className="overline mb-3">Update Fulfilment & Notify Customer</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["processing", "shipped", "delivered"].map((s) => (
                    <button key={s} onClick={() => setFulfil(s)} data-testid={`fulfil-${s}`}
                      className={`px-4 py-2 border font-mono text-xs uppercase transition-colors ${fulfil === s ? "bg-bone text-ink border-bone" : "border-line text-ash hover:border-bone hover:text-bone"}`}>
                      {s}
                    </button>
                  ))}
                </div>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="order-note"
                  placeholder="A note from the atelier (optional) — included in the email"
                  className="w-full bg-transparent border border-line px-3 py-2.5 text-bone focus:border-crimson outline-none font-mono text-xs resize-none mb-4" />
                <button onClick={saveOrderStatus} disabled={savingStatus} data-testid="save-order-status" className="btn-fill w-full py-3 overline text-xs disabled:opacity-50">
                  {savingStatus ? "Sending…" : "Update & Email Customer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#141416] border border-line w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-line">
              <p className="overline">{editId ? "Edit Piece" : "New Piece"}</p>
              <button onClick={() => setModal(false)} className="text-ash hover:text-bone"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" className="inp" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category" className="inp">
                    {["Unisex", "Womenswear", "Menswear"].map((c) => <option key={c} className="bg-ink-800">{c}</option>)}
                  </select>
                </Field>
                <Field label="Price (INR)"><input required type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="form-price" className="inp" /></Field>
              </div>
              <Field label="Fabric"><input value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} data-testid="form-fabric" className="inp" /></Field>
              <Field label="Product Images">
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {form.images.map((url, idx) => (
                    <div
                      key={idx}
                      draggable
                      onDragStart={() => setDragIdx(idx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(idx)}
                      className={`relative group aspect-[3/4] bg-ink-700 border overflow-hidden cursor-grab active:cursor-grabbing ${dragIdx === idx ? "border-crimson opacity-50" : "border-line"}`}
                      data-testid={`form-image-thumb-${idx}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
                      {idx === 0 && <span className="absolute top-1 left-1 bg-gold text-ink font-mono text-[8px] px-1">MAIN</span>}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                        {idx !== 0 && (
                          <button type="button" onClick={() => makePrimary(idx)} className="font-mono text-[9px] uppercase text-bone hover:text-gold" data-testid={`make-primary-${idx}`}>Set Main</button>
                        )}
                        <button type="button" onClick={() => removeImage(idx)} className="font-mono text-[9px] uppercase text-bone hover:text-crimson" data-testid={`remove-image-${idx}`}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <label className="aspect-[3/4] border border-dashed border-line flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-crimson text-ash hover:text-bone transition-colors" data-testid="upload-dropzone">
                    {uploading ? <Spinner size={22} className="animate-spin" /> : <UploadSimple size={22} weight="thin" />}
                    <span className="font-mono text-[9px] uppercase tracking-widest">{uploading ? "Uploading" : "Upload"}</span>
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="form-image-upload" onChange={(e) => uploadFiles(e.target.files)} disabled={uploading} />
                  </label>
                </div>
                <p className="font-mono text-[9px] text-ash uppercase tracking-widest">First image is the main / cover photo. Drag to reorder · hover to set main or remove.</p>
              </Field>
              <Field label="Sizes (comma separated)"><input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} data-testid="form-sizes" className="inp" /></Field>
              <Field label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" className="inp resize-none" /></Field>
              <label className="flex items-center gap-3 text-ash cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" className="accent-crimson w-4 h-4" />
                <span className="overline">Feature on homepage</span>
              </label>
              <button type="submit" data-testid="save-product" className="btn-fill w-full py-4 overline text-sm mt-2">{editId ? "Update Piece" : "Create Piece"}</button>
            </form>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;background:transparent;border:1px solid var(--border);padding:0.65rem 0.85rem;color:var(--text-primary);font-family:'Space Mono',monospace;font-size:0.8rem;outline:none}.inp:focus{border-color:var(--crimson)}`}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    {children}
  </div>
);

export default AdminDashboard;
